/**
 * Authentication client — replaces `supabase.auth` (GoTrue) with the NestJS
 * backend (`apps/api/src/auth`).
 *
 * WHY IT LOOKS LIKE `supabase.auth`
 * --------------------------------
 * There are 212 `supabase.auth.*` call sites across 85 files. Rewriting each one
 * into a bespoke shape would be a large, unreviewable diff and would have made
 * every call site subtly different. This exposes the same method names and the
 * same `{ data, error }` envelope, so migration is a rename:
 *
 *     auth.getSession()  →  auth.getSession()
 *
 * The one shape detail that matters: the app only ever reads `user.id`,
 * `user.email`, `session.user` and `session.access_token` (measured, not
 * assumed), so the stored user object stays deliberately small.
 *
 * WHAT CHANGED SEMANTICALLY
 * -------------------------
 * - Tokens live in `localStorage` under our own key. The Supabase client no
 *   longer manages a session at all; it forwards this token on every request
 *   which the API forwards as `request.jwt.claims`; that is what keeps RLS working.
 * - `getUser()` is answered from the stored session instead of calling a server.
 *   GoTrue's `/user` endpoint no longer exists, and a round trip to re-read a
 *   token we minted ourselves would only add latency.
 * - `getSession()` refreshes transparently when the access token is near expiry,
 *   so callers do not each need their own refresh logic.
 * - Password reset, sign-up confirmation and email change are **server-side**
 *   flows now. The link carries a single-use token hash; the page redeems it
 *   with `resetPassword()` / `confirmEmail()` rather than establishing a
 *   recovery session, so a reset works from a different browser or device.
 * - OAuth is a server-side redirect: `signInWithOAuth` navigates to the API,
 *   which owns the Google client secret, and the callback page adopts the
 *   session from the URL fragment (see `pages/AuthCallback.tsx`).
 */

import { syncSessionHint } from "./session-hint";

const STORAGE_KEY = "lyder-talk-auth:v1";

/** Refresh this many ms before expiry, so a request never races the deadline. */
const REFRESH_SKEW_MS = 60_000;

export interface AuthUser {
  id: string;
  email: string | null;
  created_at?: string;
  /** Present only during a pending email change; the flow itself is unbuilt. */
  new_email?: string | null;
}

export interface AuthSession {
  access_token: string;
  refresh_token: string;
  /** Milliseconds since epoch. */
  expires_at: number;
  user: AuthUser;
}

export interface AuthError {
  message: string;
  status?: number;
  /** Mirrors supabase-js, which sets e.g. `AuthSessionMissingError`. */
  name?: string;
}

interface Result<T> {
  data: T;
  error: AuthError | null;
}

type AuthEvent =
  | "INITIAL_SESSION"
  | "SIGNED_IN"
  | "SIGNED_OUT"
  | "TOKEN_REFRESHED"
  | "USER_UPDATED"
  // Kept in the union for compatibility. Recovery links no longer mint a
  // session — they are redeemed by `resetPassword()` — so this is never raised.
  | "PASSWORD_RECOVERY";

type Listener = (event: AuthEvent, session: AuthSession | null) => void;

function apiBaseUrl(): string | null {
  const value = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return value ? value.replace(/\/+$/, "") : null;
}

function readStored(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed?.access_token || !parsed?.refresh_token || !parsed?.user?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(session: AuthSession | null): void {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode, quota). Auth then lives only for
    // the page's lifetime rather than throwing on every render.
  }
}

class AuthClient {
  private session: AuthSession | null = readStored();
  private listeners = new Set<Listener>();
  private refreshing: Promise<AuthSession | null> | null = null;

  /* ------------------------------------------------------------------ tokens */

  /**
   * Current access token, synchronous.
   *
   * The Supabase client's fetch hook needs this on every request and cannot
   * await, so the session is held in memory and kept fresh by `getSession()`.
   */
  getAccessToken(): string | null {
    return this.session?.access_token ?? null;
  }

  /** True when the access token is missing or about to expire. */
  private needsRefresh(): boolean {
    if (!this.session) return false;
    return this.session.expires_at - Date.now() < REFRESH_SKEW_MS;
  }

  private applySession(session: AuthSession | null, event: AuthEvent): void {
    this.session = session;
    writeStored(session);
    // Keep the cross-subdomain hint in step with the session, so the marketing
    // site can forward a signed-in visitor into the app. No-op off the app
    // origin (see `session-hint.ts`).
    syncSessionHint(Boolean(session));
    for (const listener of this.listeners) {
      try {
        listener(event, session);
      } catch (error) {
        console.error("auth listener threw", error);
      }
    }
  }

  /* ------------------------------------------------------------------ reads */

  async getSession(): Promise<Result<{ session: AuthSession | null }>> {
    if (!this.session) return { data: { session: null }, error: null };

    if (this.needsRefresh()) {
      const refreshed = await this.refresh();
      if (refreshed.error) return { data: { session: null }, error: null };
      return { data: { session: this.session }, error: null };
    }

    return { data: { session: this.session }, error: null };
  }

  async getUser(): Promise<Result<{ user: AuthUser | null }>> {
    const { data } = await this.getSession();
    return { data: { user: data.session?.user ?? null }, error: null };
  }

  onAuthStateChange(callback: Listener): { data: { subscription: { unsubscribe: () => void } } } {
    this.listeners.add(callback);

    // Fire asynchronously, matching supabase-js: a listener registered during
    // render must not be called before the caller has its subscription back.
    queueMicrotask(() => {
      if (this.listeners.has(callback)) callback("INITIAL_SESSION", this.session);
    });

    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this.listeners.delete(callback);
          },
        },
      },
    };
  }

  /* ----------------------------------------------------------------- writes */

  async signInWithPassword(credentials: { email: string; password: string }): Promise<
    Result<{ user: AuthUser | null; session: AuthSession | null }>
  > {
    return this.authenticate("/v1/auth/signin", credentials);
  }

  async signUp(params: {
    email: string;
    password: string;
    options?: {
      data?: { display_name?: string; displayName?: string };
      /** Where the confirmation link should land, when confirmation is required. */
      emailRedirectTo?: string;
    };
  }): Promise<Result<{ user: AuthUser | null; session: AuthSession | null }>> {
    return this.authenticate("/v1/auth/signup", {
      email: params.email,
      password: params.password,
      displayName: params.options?.data?.display_name ?? params.options?.data?.displayName,
      emailRedirectTo: params.options?.emailRedirectTo,
    });
  }

  async signOut(_options?: { scope?: string }): Promise<{ error: AuthError | null }> {
    const refreshToken = this.session?.refresh_token;
    // Clear locally first: a failed network call must not leave the user looking
    // signed in, and the refresh token is revoked server-side best-effort.
    this.applySession(null, "SIGNED_OUT");

    if (refreshToken && apiBaseUrl()) {
      try {
        await fetch(`${apiBaseUrl()}/v1/auth/signout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Offline sign-out still succeeds locally; the token expires on its own.
      }
    }

    return { error: null };
  }

  /** Rotate the refresh token and store the new pair. */
  async refresh(): Promise<Result<{ session: AuthSession | null }>> {
    if (!this.session?.refresh_token) {
      return { data: { session: null }, error: { message: "No refresh token" } };
    }

    // Collapse concurrent refreshes: several queries mounting at once would
    // otherwise rotate the same token repeatedly, and the second rotation is a
    // replay that revokes the whole chain.
    if (this.refreshing) {
      const session = await this.refreshing;
      return { data: { session }, error: session ? null : { message: "Refresh failed" } };
    }

    this.refreshing = (async () => {
      const base = apiBaseUrl();
      if (!base) return null;

      try {
        const response = await fetch(`${base}/v1/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: this.session!.refresh_token }),
        });

        if (!response.ok) return null;

        const body = await response.json();
        const next: AuthSession = {
          access_token: body.session.accessToken,
          refresh_token: body.session.refreshToken,
          expires_at: new Date(body.session.accessTokenExpiresAt).getTime(),
          user: {
            id: body.user.id,
            email: body.user.email ?? null,
          },
        };
        this.applySession(next, "TOKEN_REFRESHED");
        return next;
      } catch {
        return null;
      } finally {
        this.refreshing = null;
      }
    })();

    const session = await this.refreshing;
    if (!session) {
      // A rejected refresh means the chain is dead (revoked, replayed or
      // expired). Staying "signed in" with an unusable token would make every
      // request 401 with no explanation.
      this.applySession(null, "SIGNED_OUT");
      return { data: { session: null }, error: { message: "Session expired" } };
    }

    return { data: { session }, error: null };
  }

  /** To keeps `auth.refreshSession()` call sites working. */
  async refreshSession(): Promise<Result<{ session: AuthSession | null }>> {
    return this.refresh();
  }

  /**
   * Update the signed-in user.
   *
   * `data` maps to the profile (display name), `password` changes the credential,
   * and `email` starts a change: the API emails a confirmation link to the *new*
   * address and leaves the account on the current one until it is confirmed.
   */
  async updateUser(
    attributes: { password?: string; email?: string; data?: { display_name?: string } },
    options?: { emailRedirectTo?: string },
  ): Promise<Result<{ user: AuthUser | null }>> {
    const base = apiBaseUrl();
    if (!base) return { data: { user: null }, error: { message: "API URL not configured" } };

    const { data: sessionData } = await this.getSession();
    const token = sessionData.session?.access_token;
    if (!token) return { data: { user: null }, error: { message: "Not signed in" } };

    const response = await fetch(`${base}/v1/auth/user`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...attributes, emailRedirectTo: options?.emailRedirectTo }),
    });
    const body = await response.json().catch(() => null);

    if (!response.ok) {
      return { data: { user: null }, error: { message: body?.message ?? "Update failed", status: response.status } };
    }

    if (this.session) this.applySession({ ...this.session, user: body.user }, "USER_UPDATED");
    return { data: { user: body.user }, error: null };
  }

  /** Adopt an externally-issued token pair (used after OAuth redirects, if added). */
  async setSession(tokens: { access_token: string; refresh_token: string }): Promise<Result<{ session: AuthSession | null }>> {
    const base = apiBaseUrl();
    if (!base) return { data: { session: null }, error: { message: "API URL not configured" } };

    const response = await fetch(`${base}/v1/auth/me`, {
      headers: { Authorization: `Bearer ${tokens.access_token}` },
    });
    if (!response.ok) return { data: { session: null }, error: { message: "Invalid session" } };

    const body = await response.json();
    const session: AuthSession = {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      // The backend is the authority on expiry; decode from the token's exp.
      expires_at: decodeExpiry(tokens.access_token) ?? Date.now() + 3_600_000,
      user: { id: body.profile.id, email: body.profile.email ?? null, created_at: body.profile.createdAt ?? undefined },
    };
    this.applySession(session, "SIGNED_IN");
    return { data: { session }, error: null };
  }

  /* ------------------------------------------------------- emailed auth links */

  /**
   * Ask the API to email a password-reset link.
   *
   * The endpoint answers the same whether or not the address is registered, so
   * callers must show the generic "check your inbox" state rather than infer
   * anything from success.
   */
  async resetPasswordForEmail(
    email: string,
    options?: { redirectTo?: string },
  ): Promise<{ data: null; error: AuthError | null }> {
    return this.postJson("/v1/auth/password/forgot", {
      email,
      redirectTo: options?.redirectTo,
    });
  }

  /** Redeem a reset link and set the new password. No session is required. */
  async resetPassword(input: { token: string; password: string }): Promise<{
    data: { reset?: boolean } | null;
    error: AuthError | null;
  }> {
    return this.postJson<{ reset?: boolean }>("/v1/auth/password/reset", input);
  }

  /** Confirm a sign-up or email-change link. No session is required. */
  async confirmEmail(token: string): Promise<{
    data: { confirmed?: boolean; email?: string | null; type?: "signup" | "email_change" } | null;
    error: AuthError | null;
  }> {
    return this.postJson<{ confirmed?: boolean; email?: string | null; type?: "signup" | "email_change" }>(
      "/v1/auth/email/confirm",
      { token },
    );
  }

  /** Send another sign-up confirmation link (same non-enumerating answer). */
  async resendConfirmation(
    email: string,
    options?: { redirectTo?: string },
  ): Promise<{ data: null; error: AuthError | null }> {
    return this.postJson("/v1/auth/email/resend", {
      email,
      redirectTo: options?.redirectTo,
    });
  }

  async exchangeCodeForSession(_code: string): Promise<{ data: null; error: AuthError }> {
    // Google's authorization code is exchanged by the API, never by the browser,
    // because the exchange needs GOOGLE_CLIENT_SECRET. The callback page adopts
    // the session the API hands back in the URL fragment instead.
    return { data: null, error: { message: "OAuth codes are exchanged by the API, not the client." } };
  }

  async verifyOtp(_params: unknown): Promise<{ data: null; error: AuthError }> {
    return { data: null, error: { message: "One-time codes are not available yet." } };
  }

  /**
   * Start an OAuth sign-in.
   *
   * Both legs of the flow are browser navigations, so this does not return a
   * session — it hands the page to the API, which hands it to Google, and the
   * callback page adopts the session on the way back. The promise resolves
   * before the navigation starts (with no error) so a caller's `finally` cannot
   * leave a spinner spinning in the way of the redirect.
   *
   * `redirectTo` must point at this app's `/auth/callback`; the API validates
   * its origin against first-party hosts before signing it into the OAuth state.
   */
  async signInWithOAuth(params: {
    provider: "google" | string;
    options?: {
      redirectTo?: string;
      queryParams?: Record<string, string>;
    };
  }): Promise<{ data: null; error: AuthError | null }> {
    const base = apiBaseUrl();
    if (!base) return { data: null, error: { message: "API URL not configured" } };

    if (params.provider !== "google") {
      return { data: null, error: { message: `${params.provider} sign-in is not available.` } };
    }

    if (typeof window === "undefined") {
      return { data: null, error: { message: "OAuth sign-in requires a browser" } };
    }

    const redirectTo = params.options?.redirectTo ?? `${window.location.origin}/auth/callback`;
    const url = new URL(`${base}/v1/auth/oauth/google/start`);
    url.searchParams.set("redirect_to", redirectTo);

    // The redirect is the result: the page leaves before anything else happens.
    window.location.assign(url.toString());
    return { data: null, error: null };
  }

  /* ----------------------------------------------------------------- private */

  /**
   * POST a small JSON body and normalise the response.
   *
   * The emailed-auth endpoints have nothing to do with the stored session, so
   * they share this instead of `authenticate()` — which would apply a session
   * that these routes never return.
   */
  private async postJson<T = null>(
    path: string,
    body: Record<string, unknown>,
  ): Promise<{ data: T | null; error: AuthError | null }> {
    const base = apiBaseUrl();
    if (!base) return { data: null, error: { message: "API URL not configured" } };

    try {
      const response = await fetch(`${base}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          data: null,
          error: { message: payload?.message ?? "Request failed", status: response.status },
        };
      }

      return { data: (payload ?? null) as T | null, error: null };
    } catch (error) {
      return {
        data: null,
        error: { message: error instanceof Error ? error.message : "Network error" },
      };
    }
  }

  private async authenticate(
    path: string,
    body: Record<string, unknown>,
  ): Promise<Result<{ user: AuthUser | null; session: AuthSession | null }>> {
    const base = apiBaseUrl();
    if (!base) {
      return { data: { user: null, session: null }, error: { message: "API URL not configured" } };
    }

    try {
      const response = await fetch(`${base}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          data: { user: null, session: null },
          error: { message: payload?.message ?? "Sign-in failed", status: response.status },
        };
      }

      if (!payload?.session) {
        // Sign-up succeeded but requires email confirmation.
        return {
          data: { user: { id: payload.user.id, email: payload.user.email ?? null }, session: null },
          error: null,
        };
      }

      const session: AuthSession = {
        access_token: payload.session.accessToken,
        refresh_token: payload.session.refreshToken,
        expires_at: new Date(payload.session.accessTokenExpiresAt).getTime(),
        user: { id: payload.user.id, email: payload.user.email ?? null },
      };
      this.applySession(session, "SIGNED_IN");
      return { data: { user: session.user, session }, error: null };
    } catch (error) {
      return {
        data: { user: null, session: null },
        error: { message: error instanceof Error ? error.message : "Network error" },
      };
    }
  }
}

/** Read `exp` without verifying — the backend already verified it. */
function decodeExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export const auth = new AuthClient();
